import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
} from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import GridViewIcon from "@mui/icons-material/GridView";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EngineeringIcon from "@mui/icons-material/Engineering";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import GavelIcon from "@mui/icons-material/Gavel";
import SchoolIcon from "@mui/icons-material/School";
import DescriptionIcon from "@mui/icons-material/Description";
import SecurityIcon from "@mui/icons-material/Security";
import ArticleIcon from "@mui/icons-material/Article";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { useState, useEffect, useMemo } from "react";
import Header from "../../components/Header";
import { getPDFList, getDownloadPdf } from "../../api";

// Visual language borrowed from scenes/lecsManagement: flat dark "glass"
// panels with a thin teal-tinted border instead of elevation/boxShadow,
// a single teal accent for active/interactive states, and uppercase
// letter-spaced micro-labels for section headers.
const ACCENT = "#449095";
const ACCENT_SOFT = "rgba(69,144,149,0.14)";
const ACCENT_BORDER = "rgba(69,144,149,0.25)";
const PANEL_BORDER = "1px solid rgba(69,144,149,0.15)";
const PANEL_BG = "rgba(20,20,20,0.8)";
const HOVER_BG = "rgba(255,255,255,0.04)";
const RED_ACCENT = "#c0392b";

const panelSx = {
  border: PANEL_BORDER,
  borderRadius: 2,
  backgroundColor: PANEL_BG,
};

const microLabelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#9a9a9a",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const usefulLinks = [
  {
    label: "ACN - National Cybersecurity Agency (Italy)",
    url: "https://www.acn.gov.it/portale/w/sa-7393",
  },
];

const DOC_TYPES = ["Technical", "Commercial", "Compliance", "Academy"];

const TYPE_META = {
  Technical: { label: "Technical", icon: EngineeringIcon },
  Commercial: { label: "Sales", icon: MonetizationOnIcon },
  Compliance: { label: "Compliance", icon: GavelIcon },
  Academy: { label: "Academy", icon: SchoolIcon },
};

// Keyword -> icon lookup so section cards/tree nodes reflect their content
// instead of a generic folder glyph.
const SECTION_ICON_RULES = [
  [/compliance|gdpr|iso|privacy|legal/i, VerifiedUserIcon],
  [/security|cyber|risk/i, SecurityIcon],
  [/price|listino|quote|offer/i, PriceCheckIcon],
  [/manual|guide|handbook/i, MenuBookIcon],
  [/slide|presentation|deck/i, SlideshowIcon],
  [/datasheet|brochure|whitepaper/i, ArticleIcon],
  [/setup|install|config|troubleshoot/i, BuildCircleIcon],
];

const getSectionIcon = (sectionName = "") => {
  const match = SECTION_ICON_RULES.find(([regex]) => regex.test(sectionName));
  return match ? match[1] : DescriptionIcon;
};

const MIME_TYPES = {
  pdf: "application/pdf",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  zip: "application/zip",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

const getFileExtension = (file = "") => file.split(".").pop().toLowerCase();

const getFileIcon = (file) => {
  const ext = getFileExtension(file);
  if (ext === "pdf") return PictureAsPdfIcon;
  if (["mp4", "mov", "avi"].includes(ext)) return VideoFileIcon;
  return InsertDriveFileIcon;
};

const getFileIconColor = (file) => {
  const ext = getFileExtension(file);
  if (ext === "pdf") return RED_ACCENT;
  if (["mp4", "mov", "avi"].includes(ext)) return ACCENT;
  return "#8a8a8a";
};

const Downloads2 = () => {
  const [docsByType, setDocsByType] = useState({
    Technical: [],
    Commercial: [],
    Compliance: [],
    Academy: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("tree");

  useEffect(() => {
    Promise.all(DOC_TYPES.map((type) => getPDFList({ type })))
      .then((results) => {
        setDocsByType({
          Technical: results[0].data,
          Commercial: results[1].data,
          Compliance: results[2].data,
          Academy: results[3].data,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSectionClick = (type, sectionObj) => {
    setSelectedType(type);
    setSelectedSection(sectionObj.section);
    setSelectedFiles(sectionObj.files);
  };

  const handleDownload = (file, type, section) => {
    const ext = getFileExtension(file);
    getDownloadPdf({ type, section, name: file }).then((res) => {
      const blob = new Blob([res.data], { type: MIME_TYPES[ext] || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  // Search now runs across every type/group at once, since browsing is no
  // longer siloed behind tabs.
  const filteredDocsByType = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const result = {};
    DOC_TYPES.forEach((type) => {
      const docs = docsByType[type] || [];
      if (!query) {
        result[type] = docs;
        return;
      }
      result[type] = docs
        .map((sectionObj) => ({
          ...sectionObj,
          files: sectionObj.files.filter((file) => file.toLowerCase().includes(query)),
        }))
        .filter(
          (sectionObj) =>
            sectionObj.section.toLowerCase().includes(query) || sectionObj.files.length > 0
        );
    });
    return result;
  }, [docsByType, searchQuery]);

  const totalResults = DOC_TYPES.reduce(
    (sum, type) => sum + (filteredDocsByType[type]?.length || 0),
    0
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        color: "#777",
      }}
    >
      <SearchOffIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography variant="body2">
        {searchQuery ? "No documents match your search." : "No documents available yet."}
      </Typography>
    </Box>
  );

  const renderLoadingSkeleton = () => (
    <Box sx={{ px: 4, pb: 4 }}>
      {DOC_TYPES.map((type) => (
        <Skeleton
          key={type}
          variant="rounded"
          height={56}
          sx={{ mb: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)" }}
        />
      ))}
    </Box>
  );

  const renderSectionCards = (type, docs) => (
    <Grid container spacing={2.5} sx={{ padding: 4, paddingTop: 2 }}>
      {docs.map((sectionObj, index) => {
        const SectionIcon = getSectionIcon(sectionObj.section);
        return (
          <Grid xs={12} sm={6} size={4} key={index}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                position: "relative",
                border: "1px solid rgba(255,255,255,0.08)",
                backgroundColor: "rgba(255,255,255,0.02)",
                transition: "all 0.15s",
                '&:hover': {
                  borderColor: ACCENT,
                  backgroundColor: HOVER_BG,
                },
              }}
            >
              <Chip
                size="small"
                label={`${sectionObj.files.length} file${sectionObj.files.length !== 1 ? "s" : ""}`}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  bgcolor: ACCENT_SOFT,
                  color: ACCENT,
                  border: `1px solid ${ACCENT_BORDER}`,
                  fontWeight: 600,
                }}
              />
              <CardActionArea onClick={() => handleSectionClick(type, sectionObj)}>
                <Box
                  sx={{
                    height: "160px",
                    bgcolor: "rgba(255,255,255,0.03)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <SectionIcon sx={{ fontSize: 64, color: ACCENT }} />
                </Box>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="body1" fontWeight="600" sx={{ color: "#e8e8e8" }}>
                    {sectionObj.section}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderTreeView = () => (
    <Box sx={{ padding: 4, paddingTop: 1 }}>
      {DOC_TYPES.map((type) => {
        const docs = filteredDocsByType[type] || [];
        if (searchQuery && docs.length === 0) return null;
        const { label, icon: TypeIcon } = TYPE_META[type];
        const fileCount = docs.reduce((sum, s) => sum + s.files.length, 0);

        return (
          <Accordion
            key={type}
            defaultExpanded={!!searchQuery}
            elevation={0}
            sx={{
              backgroundColor: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.07)",
              "&:before": { display: "none" },
              mb: 1.5,
              borderRadius: "10px !important",
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#9a9a9a" }} />}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                <TypeIcon sx={{ color: ACCENT }} />
                <Typography sx={{ ...microLabelSx, fontSize: "0.78rem", flexGrow: 1, color: "#e8e8e8" }}>
                  {label}
                </Typography>
                <Chip
                  size="small"
                  label={`${docs.length} folder${docs.length !== 1 ? "s" : ""} · ${fileCount} file${fileCount !== 1 ? "s" : ""}`}
                  sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "#ccc", border: "1px solid rgba(255,255,255,0.08)" }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "rgba(0,0,0,0.25)", p: docs.length ? 1 : 2 }}>
              {docs.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#777", px: 2, py: 1 }}>
                  No documents in this group.
                </Typography>
              ) : (
                docs.map((sectionObj, index) => {
                  const SectionIcon = getSectionIcon(sectionObj.section);
                  return (
                    <Accordion
                      key={index}
                      defaultExpanded={!!searchQuery}
                      elevation={0}
                      sx={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        "&:before": { display: "none" },
                        borderLeft: "3px solid transparent",
                        transition: "all 0.15s",
                        mb: 0.5,
                        "&:hover": {
                          borderLeftColor: ACCENT,
                          backgroundColor: HOVER_BG,
                        },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#9a9a9a" }} fontSize="small" />}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                          <SectionIcon sx={{ color: "#8a8a8a" }} fontSize="small" />
                          <Typography sx={{ flexGrow: 1, fontSize: "0.85rem", color: "#ddd" }}>
                            {sectionObj.section}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${sectionObj.files.length} file${sectionObj.files.length !== 1 ? "s" : ""}`}
                            sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "#aaa" }}
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List disablePadding>
                          {sectionObj.files.map((file, idx) => {
                            const FileIcon = getFileIcon(file);
                            return (
                              <ListItem disablePadding key={idx}>
                                <ListItemButton
                                  onClick={() => handleDownload(file, type, sectionObj.section)}
                                  sx={{
                                    pl: 4,
                                    borderLeft: "3px solid transparent",
                                    transition: "all 0.15s",
                                    '&:hover': { bgcolor: HOVER_BG, borderLeftColor: ACCENT },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <FileIcon sx={{ color: getFileIconColor(file) }} fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={file}
                                    primaryTypographyProps={{ fontSize: "0.85rem", color: "#ccc" }}
                                  />
                                  <SaveAltIcon sx={{ color: ACCENT }} fontSize="small" />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );

  const renderGridView = () => (
    <Box sx={{ pb: 2 }}>
      {DOC_TYPES.map((type) => {
        const docs = filteredDocsByType[type] || [];
        if (searchQuery && docs.length === 0) return null;
        const { label, icon: TypeIcon } = TYPE_META[type];

        return (
          <Box key={type} sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 4, pt: 1 }}>
              <TypeIcon sx={{ color: ACCENT, fontSize: 20 }} />
              <Typography sx={{ ...microLabelSx, color: "#e8e8e8" }}>{label}</Typography>
              <Chip
                size="small"
                label={docs.length}
                sx={{ bgcolor: "rgba(255,255,255,0.05)", color: "#ccc", border: "1px solid rgba(255,255,255,0.08)" }}
              />
            </Stack>
            {docs.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#777", px: 4, py: 2 }}>
                No documents in this group.
              </Typography>
            ) : (
              renderSectionCards(type, docs)
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      <Box sx={{ paddingLeft: "20px" }}>
        <Header title="Documentation" subtitle="" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          marginTop: 2,
          padding: 3,
          maxWidth: "95%",
          mx: "auto",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <LinkIcon sx={{ color: ACCENT, fontSize: 18 }} />
          <Typography sx={microLabelSx}>Useful Links</Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {usefulLinks.map((link, idx) => (
            <Chip
              key={idx}
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              icon={<OpenInNewIcon sx={{ fontSize: 16, color: `${ACCENT} !important` }} />}
              label={link.label}
              sx={{
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#e8e8e8",
                fontWeight: 500,
                px: 1,
                py: 2.2,
                transition: "all 0.15s",
                "&:hover": {
                  bgcolor: ACCENT_SOFT,
                  borderColor: ACCENT,
                },
              }}
            />
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...panelSx,
          marginTop: 3,
          paddingBottom: 4,
          paddingTop: 3,
          maxWidth: "95%",
          mx: "auto",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 4, mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search documents or folders across all groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: 320,
              "& .MuiOutlinedInput-root": {
                color: "#e8e8e8",
                "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                "&:hover fieldset": { borderColor: ACCENT },
                "&.Mui-focused fieldset": { borderColor: ACCENT },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8a8a8a" }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            sx={{
              "& .MuiToggleButton-root": {
                color: "#9a9a9a",
                borderColor: "rgba(255,255,255,0.15)",
                textTransform: "none",
              },
              "& .Mui-selected": {
                color: `${ACCENT} !important`,
                backgroundColor: `${ACCENT_SOFT} !important`,
                borderColor: `${ACCENT} !important`,
              },
            }}
          >
            <ToggleButton value="tree">
              <AccountTreeIcon sx={{ mr: 1 }} fontSize="small" />
              Tree
            </ToggleButton>
            <ToggleButton value="grid">
              <GridViewIcon sx={{ mr: 1 }} fontSize="small" />
              Grid
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {loading ? (
          renderLoadingSkeleton()
        ) : totalResults === 0 ? (
          renderEmptyState()
        ) : viewMode === "tree" ? (
          renderTreeView()
        ) : (
          renderGridView()
        )}
      </Paper>

      <Dialog
        open={!!selectedSection}
        onClose={() => setSelectedSection(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { ...panelSx, backgroundColor: "#141414" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            backgroundColor: "#111",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            color: "#eaeaea",
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: "#777" }} />}
            sx={{ color: "#eaeaea", "& .MuiBreadcrumbs-li": { color: "#eaeaea" } }}
          >
            <Typography sx={{ color: "#9a9a9a" }}>{TYPE_META[selectedType]?.label}</Typography>
            <Typography sx={{ color: ACCENT, fontWeight: 700 }}>{selectedSection}</Typography>
          </Breadcrumbs>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List>
            {selectedFiles.map((file, idx) => {
              const FileIcon = getFileIcon(file);
              return (
                <ListItem disablePadding key={idx}>
                  <ListItemButton
                    onClick={() => handleDownload(file, selectedType, selectedSection)}
                    sx={{
                      borderLeft: "3px solid transparent",
                      transition: "all 0.15s",
                      '&:hover': { bgcolor: HOVER_BG, borderLeftColor: ACCENT },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FileIcon sx={{ color: getFileIconColor(file) }} />
                    </ListItemIcon>
                    <ListItemText primary={file} primaryTypographyProps={{ color: "#ddd" }} />
                    <SaveAltIcon sx={{ color: ACCENT }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Downloads2;
